import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Flex, Box, Spinner } from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { LuSettings, LuEye, LuPalette, LuPencil, LuInfo, LuQrCode, LuType } from 'react-icons/lu';
import { type DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import Header from '../components/Header';
import ImagePickerModal from '../components/ImagePickerModal';
import QrCodeModal from '../components/QrCodeModal';
import MenuContent from '../components/MenuContent';
import SettingsModal from '../components/menu/SettingsModal';
import StoreInfoModal from '../components/menu/StoreInfoModal';
import ThemeModal from '../components/menu/ThemeModal';
import CategoryModal from '../components/menu/CategoryModal';
import ItemModal from '../components/menu/ItemModal';
import {
  getMenu,
  deleteMenu,
  updateItem,
  deleteItem,
  uploadItemImage,
  updateCategory,
  getBadgeDefinitions,
  type MenuResponse,
  type CategoryResponse,
  type MenuItemResponse,
  type MenuTheme,
  type BadgeDefinition,
  DEFAULT_MENU_THEME,
  reorderCategories,
  reorderItems,
  uploadCategoryImage,
  updateMenu,
  uploadMenuHeaderImage,
} from '../api/menus';

const MenuDetail = () => {
  const { t } = useTranslation();
  const { businessId, menuId } = useParams<{ businessId: string; menuId: string }>();
  const navigate = useNavigate();

  // Core state
  const [menu, setMenu] = useState<MenuResponse | null>(null);
  const [menuTheme, setMenuTheme] = useState<MenuTheme>({ ...DEFAULT_MENU_THEME });
  const [badgeDefinitions, setBadgeDefinitions] = useState<BadgeDefinition[]>([]);
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [viewLang, setViewLang] = useState('en');

  // Modal visibility
  const [showSettings, setShowSettings] = useState(false);
  const [showStoreInfo, setShowStoreInfo] = useState(false);
  const [showTheme, setShowTheme] = useState(false);
  const [showQr, setShowQr] = useState(false);

  // Theme & badge drafts (need to live here so MenuContent preview updates in real-time)
  const [draftTheme, setDraftTheme] = useState<MenuTheme>({ ...DEFAULT_MENU_THEME });
  const [draftBadges, setDraftBadges] = useState<BadgeDefinition[]>([]);

  // Category modal
  const [editingCategory, setEditingCategory] = useState<CategoryResponse | null>(null);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [pendingCategoryImage, setPendingCategoryImage] = useState<Blob | null>(null);
  const pendingCategoryImageUrl = useMemo(
    () => pendingCategoryImage ? URL.createObjectURL(pendingCategoryImage) : null,
    [pendingCategoryImage],
  );

  // Item modal
  const [editingItem, setEditingItem] = useState<MenuItemResponse | null>(null);
  const [isCreatingItem, setIsCreatingItem] = useState(false);
  const [creatingItemCategoryId, setCreatingItemCategoryId] = useState<string | null>(null);

  // Edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [isMobileQuickEditMode, setIsMobileQuickEditMode] = useState(false);
  const [isDesktopEditMode, setIsDesktopEditMode] = useState(false);
  const [isDesktopQuickEditMode, setIsDesktopQuickEditMode] = useState(false);
  const [currentPage, setCurrentPage] = useState<1 | 2>(1);

  // Quick edit image
  const [quickEditImageTarget, setQuickEditImageTarget] = useState<{ type: 'item' | 'category' | 'header'; id: string } | null>(null);
  const [showQuickEditImagePicker, setShowQuickEditImagePicker] = useState(false);

  // --- Data fetching ---

  const initialLoadDone = useRef(false);

  const { isLoading, isFetching } = useQuery({
    queryKey: ['menu', menuId],
    queryFn: async () => {
      const data = await getMenu(menuId!);
      setMenu(data);
      setDefaultLanguage(data.default_language || 'en');
      if (!initialLoadDone.current) {
        setViewLang(data.default_language || 'en');
        initialLoadDone.current = true;
      }
      setMenuTheme(data.theme || { ...DEFAULT_MENU_THEME });
      try {
        const badges = await getBadgeDefinitions(data.id);
        setBadgeDefinitions(badges);
      } catch { /* badges not available */ }
      return data;
    },
    enabled: !!menuId,
  });

  // Reset initial load flag when navigating to a different menu
  useEffect(() => {
    initialLoadDone.current = false;
  }, [menuId]);

  // --- Helpers ---

  const getTranslated = (field: { [key: string]: string } | null | undefined, lang?: string): string => {
    if (!field) return '';
    const useLang = lang || viewLang;
    return field[useLang] || field[defaultLanguage] || Object.values(field)[0] || '';
  };

  const availableLanguages = menu?.available_languages || ['en'];
  const categories = menu?.categories || [];
  const categoriesRef = useRef(categories);
  categoriesRef.current = categories;

  const menuContentCategories = useMemo(() => categories.map(cat => ({
    id: cat.id,
    name: getTranslated(cat.name),
    description: getTranslated(cat.description) || null,
    image_url: cat.image_url,
    is_visible: cat.is_visible,
    items: cat.items.map(item => ({
      id: item.id,
      name: getTranslated(item.name),
      description: getTranslated(item.description) || null,
      price: item.price,
      image_url: item.image_url,
      badges: item.badges || [],
      spicy_level: item.spicy_level,
      options: item.options?.map(o => ({ name: getTranslated(o.name), price: o.price })) || null,
      is_sold_out: item.is_sold_out,
      is_visible: item.is_visible,
    })),
  })), [categories, viewLang, defaultLanguage]);

  const activeBadges = showTheme ? draftBadges : badgeDefinitions;
  const badgeDefsForContent = useMemo(() => activeBadges.map(b => ({
    key: b.key,
    label: getTranslated(b.label),
    color: b.color,
  })), [activeBadges, viewLang, defaultLanguage]);

  // --- Handlers ---

  const handleDeleteMenu = async () => {
    if (!menuId || !businessId) return;
    try {
      await deleteMenu(menuId);
      toast.success(t('menus.menuDeleted'));
      navigate(`/businesses/${businessId}/menus`);
    } catch (error) {
      toast.error(t('menus.deleteFailed'));
      console.error(error);
    }
  };

  const openCategoryEdit = useCallback((category: CategoryResponse) => {
    setIsCreatingCategory(false);
    setEditingCategory(category);
  }, []);

  const openItemEdit = useCallback((item: MenuItemResponse) => {
    setIsCreatingItem(false);
    setCreatingItemCategoryId(null);
    setEditingItem(item);
  }, []);

  const handleAddCategory = useCallback(() => {
    setIsCreatingCategory(true);
    setEditingCategory(null);
  }, []);

  const handleAddItem = useCallback((categoryId: string) => {
    setIsCreatingItem(true);
    setCreatingItemCategoryId(categoryId);
    setEditingItem(null);
  }, []);

  const closeCategoryModal = () => {
    setEditingCategory(null);
    setIsCreatingCategory(false);
    setPendingCategoryImage(null);
  };

  const closeItemModal = () => {
    setEditingItem(null);
    setIsCreatingItem(false);
    setCreatingItemCategoryId(null);
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!menuId) return;
    try {
      const { deleteCategory } = await import('../api/menus');
      await deleteCategory(menuId, categoryId);
      setMenu(prev => prev ? { ...prev, categories: prev.categories?.filter(c => c.id !== categoryId) } : prev);
      closeCategoryModal();
    } catch (error) {
      toast.error(t('menus.deleteCategoryFailed'));
      console.error(error);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    try {
      await deleteItem(itemId);
      setMenu(prev => prev ? {
        ...prev,
        categories: prev.categories?.map(c => ({ ...c, items: c.items.filter(i => i.id !== itemId) })),
      } : prev);
      closeItemModal();
    } catch (error) {
      toast.error(t('menus.deleteItemFailed'));
      console.error(error);
    }
  };

  const handleRemoveImage = async () => {
    if (!editingItem) return;
    const original = editingItem;
    setEditingItem({ ...editingItem, image_url: null });
    setMenu(prev => prev ? {
      ...prev,
      categories: prev.categories?.map(c => ({
        ...c, items: c.items.map(i => i.id === editingItem.id ? { ...i, image_url: null } : i),
      })),
    } : prev);
    try {
      await updateItem(editingItem.id, { image_url: null });
    } catch (error) {
      setEditingItem(original);
      toast.error(t('menus.updateItemFailed'));
      console.error(error);
    }
  };

  const handleRemoveCategoryImage = async () => {
    if (!editingCategory || !menuId) return;
    const original = editingCategory;
    setEditingCategory({ ...editingCategory, image_url: null });
    setMenu(prev => prev ? {
      ...prev,
      categories: prev.categories?.map(c => c.id === editingCategory.id ? { ...c, image_url: null } : c),
    } : prev);
    try {
      await updateCategory(menuId, editingCategory.id, { image_url: null });
    } catch (error) {
      setEditingCategory(original);
      toast.error(t('menus.updateCategoryFailed'));
      console.error(error);
    }
  };

  const handleCategoryImageReady = async (blob: Blob) => {
    if (!menuId) return;
    if (!editingCategory) {
      // Creating a new category — store blob to upload after creation
      setPendingCategoryImage(blob);
      return;
    }
    try {
      const updated = await uploadCategoryImage(menuId, editingCategory.id, blob);
      setEditingCategory(updated);
      setMenu(prev => prev ? {
        ...prev, categories: prev.categories?.map(c => c.id === editingCategory.id ? updated : c),
      } : prev);
    } catch (error) {
      toast.error(t('menus.updateCategoryFailed'));
      console.error(error);
    }
  };

  const handleItemImageReady = async (blob: Blob) => {
    if (!editingItem) return;
    try {
      const updated = await uploadItemImage(editingItem.id, blob);
      setEditingItem(updated);
      setMenu(prev => prev ? {
        ...prev,
        categories: prev.categories?.map(c => ({
          ...c, items: c.items.map(i => i.id === editingItem.id ? updated : i),
        })),
      } : prev);
    } catch (error) {
      toast.error(t('menus.updateItemFailed'));
      console.error(error);
    }
  };

  const handleQuickEditImageReady = async (blob: Blob) => {
    if (!quickEditImageTarget) return;
    if (quickEditImageTarget.type === 'item') {
      try {
        const updated = await uploadItemImage(quickEditImageTarget.id, blob);
        setMenu(prev => prev ? {
          ...prev,
          categories: prev.categories?.map(c => ({
            ...c, items: c.items.map(i => i.id === quickEditImageTarget.id ? updated : i),
          })),
        } : prev);
      } catch (error) {
        toast.error(t('menus.updateItemFailed'));
        console.error(error);
      }
    } else if (quickEditImageTarget.type === 'header') {
      if (menuId) {
        try {
          const result = await uploadMenuHeaderImage(menuId, blob);
          setMenu(prev => prev ? { ...prev, header_image_url: result.header_image_url } : prev);
        } catch (error) {
          toast.error(t('menus.updateFailed'));
          console.error(error);
        }
      }
    } else if (menuId) {
      try {
        const updated = await uploadCategoryImage(menuId, quickEditImageTarget.id, blob);
        setMenu(prev => prev ? {
          ...prev, categories: prev.categories?.map(c => c.id === quickEditImageTarget.id ? updated : c),
        } : prev);
      } catch (error) {
        toast.error(t('menus.updateCategoryFailed'));
        console.error(error);
      }
    }
    setShowQuickEditImagePicker(false);
    setQuickEditImageTarget(null);
  };

  // Drag and drop
  const handleCategoryDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !menuId) return;
    const cats = categoriesRef.current;
    const oldIndex = cats.findIndex(c => c.id === active.id);
    const newIndex = cats.findIndex(c => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(cats, oldIndex, newIndex);
    setMenu(prev => prev ? { ...prev, categories: reordered } : prev);
    try { await reorderCategories(menuId, reordered.map(c => c.id)); }
    catch (error) { console.error('Reorder categories failed:', error); }
  }, [menuId]);

  const handleItemDragEnd = useCallback(async (categoryId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !menuId) return;
    const category = categoriesRef.current.find(c => c.id === categoryId);
    if (!category) return;
    const oldIndex = category.items.findIndex(i => i.id === active.id);
    const newIndex = category.items.findIndex(i => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reorderedItems = arrayMove(category.items, oldIndex, newIndex);
    setMenu(prev => prev ? {
      ...prev, categories: prev.categories?.map(c => c.id === categoryId ? { ...c, items: reorderedItems } : c),
    } : prev);
    try { await reorderItems(categoryId, reorderedItems.map(i => i.id)); }
    catch (error) { console.error('Reorder items failed:', error); }
  }, [menuId]);

  const openThemeModal = () => {
    setDraftTheme({ ...menuTheme });
    setDraftBadges(badgeDefinitions.map(b => ({ ...b })));
    setShowTheme(true);
  };

  // --- Edit mode callbacks shared between desktop & mobile MenuContent ---
  const onTitleClick = useCallback(() => setShowSettings(true), []);
  const onStoreInfoClick = useCallback(() => setShowStoreInfo(true), []);
  const onHeaderImageClick = useCallback(() => setShowSettings(true), []);

  const onCategoryClick = useCallback((categoryId: string) => {
    const cat = categoriesRef.current.find(c => c.id === categoryId);
    if (cat) openCategoryEdit(cat);
  }, [openCategoryEdit]);

  const onItemClick = useCallback((itemId: string) => {
    for (const cat of categoriesRef.current) {
      const item = cat.items.find(i => i.id === itemId);
      if (item) { openItemEdit(item); break; }
    }
  }, [openItemEdit]);

  // --- Quick edit callbacks ---

  const handleQuickEditItem = useCallback(async (itemId: string, updates: { name?: string; description?: string; price?: number; is_visible?: boolean; is_sold_out?: boolean }) => {
    let foundItem: MenuItemResponse | undefined;
    for (const cat of categoriesRef.current) {
      foundItem = cat.items.find(i => i.id === itemId);
      if (foundItem) break;
    }
    if (!foundItem) return;

    const apiUpdates: Parameters<typeof updateItem>[1] = {};
    if (updates.name !== undefined) {
      apiUpdates.name = { ...foundItem.name, [viewLang]: updates.name };
    }
    if (updates.description !== undefined) {
      apiUpdates.description = { ...(foundItem.description || {}), [viewLang]: updates.description };
    }
    if (updates.price !== undefined) {
      apiUpdates.price = updates.price;
    }
    if (updates.is_visible !== undefined) {
      apiUpdates.is_visible = updates.is_visible;
    }
    if (updates.is_sold_out !== undefined) {
      apiUpdates.is_sold_out = updates.is_sold_out;
    }

    const snapshot = foundItem;
    setMenu(prev => prev ? {
      ...prev,
      categories: prev.categories?.map(c => ({
        ...c,
        items: c.items.map(i => {
          if (i.id !== itemId) return i;
          return {
            ...i,
            ...(apiUpdates.name ? { name: apiUpdates.name } : {}),
            ...(apiUpdates.description ? { description: apiUpdates.description } : {}),
            ...(apiUpdates.price !== undefined ? { price: apiUpdates.price } : {}),
            ...(apiUpdates.is_visible !== undefined ? { is_visible: apiUpdates.is_visible } : {}),
            ...(apiUpdates.is_sold_out !== undefined ? { is_sold_out: apiUpdates.is_sold_out } : {}),
          };
        }),
      })),
    } : prev);

    try {
      await updateItem(itemId, apiUpdates);
    } catch (error) {
      toast.error(t('menus.updateItemFailed'));
      console.error(error);
      setMenu(prev => prev ? {
        ...prev,
        categories: prev.categories?.map(c => ({
          ...c,
          items: c.items.map(i => i.id === itemId ? snapshot : i),
        })),
      } : prev);
    }
  }, [viewLang, t]);

  const handleQuickEditCategory = useCallback(async (categoryId: string, updates: { name?: string; is_visible?: boolean }) => {
    const cat = categoriesRef.current.find(c => c.id === categoryId);
    if (!cat || !menuId) return;

    const apiUpdates: Parameters<typeof updateCategory>[2] = {};
    if (updates.name !== undefined) {
      apiUpdates.name = { ...cat.name, [viewLang]: updates.name };
    }
    if (updates.is_visible !== undefined) {
      apiUpdates.is_visible = updates.is_visible;
    }

    const snapshot = cat;
    setMenu(prev => prev ? {
      ...prev,
      categories: prev.categories?.map(c => {
        if (c.id !== categoryId) return c;
        return {
          ...c,
          ...(apiUpdates.name ? { name: apiUpdates.name } : {}),
          ...(apiUpdates.is_visible !== undefined ? { is_visible: apiUpdates.is_visible } : {}),
        };
      }),
    } : prev);

    try {
      await updateCategory(menuId, categoryId, apiUpdates);
    } catch (error) {
      toast.error(t('menus.updateCategoryFailed'));
      console.error(error);
      setMenu(prev => prev ? {
        ...prev,
        categories: prev.categories?.map(c => c.id === categoryId ? snapshot : c),
      } : prev);
    }
  }, [menuId, viewLang, t]);

  const handleQuickEditItemImage = useCallback((itemId: string) => {
    setQuickEditImageTarget({ type: 'item', id: itemId });
    setShowQuickEditImagePicker(true);
  }, []);

  const handleQuickEditCategoryImage = useCallback((categoryId: string) => {
    setQuickEditImageTarget({ type: 'category', id: categoryId });
    setShowQuickEditImagePicker(true);
  }, []);

  const handleQuickEditTitle = useCallback(async (newName: string) => {
    if (!menu || !menuId) return;
    const snapshot = menu;
    setMenu(prev => prev ? { ...prev, name: { ...prev.name, [viewLang]: newName } } : prev);
    try {
      await updateMenu(menuId, { name: { ...menu.name, [viewLang]: newName } });
    } catch (error) {
      toast.error(t('menus.updateFailed', 'Update failed'));
      console.error(error);
      setMenu(snapshot);
    }
  }, [menu, menuId, viewLang, t]);

  const handleQuickEditDescription = useCallback(async (newDesc: string) => {
    if (!menu || !menuId) return;
    const snapshot = menu;
    setMenu(prev => prev ? { ...prev, description: { ...(prev.description || {}), [viewLang]: newDesc } } : prev);
    try {
      await updateMenu(menuId, { description: { ...(menu.description || {}), [viewLang]: newDesc } });
    } catch (error) {
      toast.error(t('menus.updateFailed', 'Update failed'));
      console.error(error);
      setMenu(snapshot);
    }
  }, [menu, menuId, viewLang, t]);

  const handleQuickEditHeaderImage = useCallback(() => {
    setQuickEditImageTarget({ type: 'header', id: menuId! });
    setShowQuickEditImagePicker(true);
  }, [menuId]);

  const quickEditCallbacks = useMemo(() => ({
    onQuickEditItem: handleQuickEditItem,
    onQuickEditItemImage: handleQuickEditItemImage,
    onQuickEditCategory: handleQuickEditCategory,
    onQuickEditCategoryImage: handleQuickEditCategoryImage,
    onQuickEditTitle: handleQuickEditTitle,
    onQuickEditDescription: handleQuickEditDescription,
    onQuickEditHeaderImage: handleQuickEditHeaderImage,
  }), [handleQuickEditItem, handleQuickEditItemImage, handleQuickEditCategory, handleQuickEditCategoryImage, handleQuickEditTitle, handleQuickEditDescription, handleQuickEditHeaderImage]);


  const editModeCallbacks = useMemo(() => ({
    onCategoryClick,
    onItemClick,
    onAddCategory: handleAddCategory,
    onAddItem: handleAddItem,
    onReorderCategories: handleCategoryDragEnd,
    onReorderItems: handleItemDragEnd,
    onTitleClick,
    onStoreInfoClick,
    onHeaderImageClick,
  }), [onCategoryClick, onItemClick, handleAddCategory, handleAddItem, handleCategoryDragEnd, handleItemDragEnd, onTitleClick, onStoreInfoClick, onHeaderImageClick]);

  const editModeProps = useMemo(() => isEditMode ? editModeCallbacks : {}, [isEditMode, editModeCallbacks]);

  const activeTheme = showTheme ? draftTheme : menuTheme;
  const sortedLanguages = useMemo(() => [defaultLanguage, ...availableLanguages.filter(l => l !== defaultLanguage)], [defaultLanguage, availableLanguages]);

  const menuContentBaseProps = useMemo(() => ({
    name: getTranslated(menu?.name),
    description: getTranslated(menu?.description) || null,
    currency: menu?.currency || 'USD',
    categories: menuContentCategories,
    theme: activeTheme,
    storeInfo: menu?.store_info || undefined,
    badgeDefinitions: badgeDefsForContent,
    headerImageUrl: menu?.header_image_url,
    availableLanguages: sortedLanguages,
    currentLanguage: viewLang,
    onLanguageChange: setViewLang,
    page: currentPage,
    onPageChange: setCurrentPage,
  }), [menu, menuContentCategories, activeTheme, badgeDefsForContent, sortedLanguages, viewLang, currentPage]);

  // Desktop: toggle edit mode
  const desktopMenuContentProps = useMemo(() => ({
    ...menuContentBaseProps,
    ...(isDesktopEditMode ? editModeCallbacks : {}),
    ...(isDesktopQuickEditMode ? quickEditCallbacks : {}),
  }), [menuContentBaseProps, isDesktopEditMode, editModeCallbacks, isDesktopQuickEditMode, quickEditCallbacks]);

  // Mobile: toggle edit mode
  const mobileMenuContentProps = useMemo(() => ({
    ...menuContentBaseProps,
    ...editModeProps,
    ...(isMobileQuickEditMode ? quickEditCallbacks : {}),
  }), [menuContentBaseProps, editModeProps, isMobileQuickEditMode, quickEditCallbacks]);

  // --- Loading / error states ---

  if (isLoading || (!menu && isFetching)) {
    return (
      <Flex minHeight="100vh" direction="column" backgroundColor="neutral.900">
        <Header businessId={businessId} />
        <Flex flex="1" justifyContent="center" alignItems="center">
          <Spinner size="xl" color="neutral.300" />
        </Flex>
      </Flex>
    );
  }

  if (!menu) {
    return (
      <Flex minHeight="100vh" direction="column" backgroundColor="neutral.900">
        <Header businessId={businessId} />
        <Flex flex="1" justifyContent="center" alignItems="center">
          <Box color="neutral.400" fontSize="14px">{t('menus.menuNotFound')}</Box>
        </Flex>
      </Flex>
    );
  }

  // --- Page toggle options ---
  const pageOptions: { page: 1 | 2; label: string }[] = [
    { page: 1, label: t('menus.categories', 'Categories') },
    { page: 2, label: t('menus.items', 'Items') },
  ];

  return (
    <Flex height={{ md: '100vh' }} minHeight={{ base: '100vh', md: 'unset' }} direction="column" backgroundColor="neutral.900">
      <Box display={{ base: 'none', md: 'block' }} flexShrink={0}>
        <Header businessId={businessId} />
      </Box>

      {/* Desktop layout */}
      <Flex flex="1" overflow="hidden" display={{ base: 'none', md: 'flex' }} minHeight={0}>
        {/* Center - menu preview (only scrollable area) */}
        <Flex flex="1" direction="column" overflow="auto">
          <Flex
            justifyContent="center"
            alignItems="center"
            position="sticky"
            top="0"
            zIndex="5"
            backgroundColor="neutral.900"
            py="12px"
            px="24px"
            flexShrink={0}
          >
            <Box maxWidth="480px" width="100%" position="relative">
              <Flex justifyContent="center" alignItems="center">
                {menuContentCategories.length > 1 ? (
                  <Flex backgroundColor="neutral.700" borderRadius="8px" p="3px" gap="2px">
                    {pageOptions.map(({ page, label }) => (
                      <Box
                        key={page}
                        px="14px"
                        py="5px"
                        borderRadius="6px"
                        fontSize="12px"
                        fontWeight="500"
                        cursor="pointer"
                        color={currentPage === page ? 'neutral.100' : 'neutral.500'}
                        backgroundColor={currentPage === page ? 'neutral.600' : 'transparent'}
                        onClick={() => setCurrentPage(page)}
                      >
                        {label}
                      </Box>
                    ))}
                  </Flex>
                ) : (
                  <Box flex="1" />
                )}
                <Flex alignItems="center" gap="4px" position="absolute" right="0" backgroundColor="neutral.700" borderRadius="8px" p="3px">
                  <Box
                    px="14px"
                    py="5px"
                    borderRadius="6px"
                    fontSize="12px"
                    fontWeight="500"
                    cursor="pointer"
                    color={isDesktopQuickEditMode ? 'neutral.100' : 'neutral.500'}
                    backgroundColor={isDesktopQuickEditMode ? 'neutral.600' : 'transparent'}
                    onClick={() => {
                      const next = !isDesktopQuickEditMode;
                      setIsDesktopQuickEditMode(next);
                      if (next) setIsDesktopEditMode(false);
                    }}
                  >
                    {t('menus.quickEdit', 'Quick Edit')}
                  </Box>
                  <Box
                    px="14px"
                    py="5px"
                    borderRadius="6px"
                    fontSize="12px"
                    fontWeight="500"
                    cursor="pointer"
                    color={isDesktopEditMode ? 'neutral.100' : 'neutral.500'}
                    backgroundColor={isDesktopEditMode ? 'neutral.600' : 'transparent'}
                    onClick={() => {
                      const next = !isDesktopEditMode;
                      setIsDesktopEditMode(next);
                      if (next) setIsDesktopQuickEditMode(false);
                    }}
                  >
                    {t('menus.edit')}
                  </Box>
                </Flex>
              </Flex>
            </Box>
          </Flex>
          <Flex flex="1" justifyContent="center" py="20px" px="24px">
            <Box maxWidth="480px" width="100%" position="relative">
              <Box
                borderWidth="1px"
                borderColor="neutral.700"
                borderRadius="12px"
                overflow="hidden"
                position="relative"
                boxShadow="0 4px 24px rgba(0,0,0,0.3)"
              >
                <MenuContent {...desktopMenuContentProps} />
              </Box>
            </Box>
          </Flex>
        </Flex>

        {/* Right sidebar - fixed height, no scroll */}
        <Flex direction="column" width="240px" flexShrink={0} borderLeftWidth="1px" borderColor="neutral.700" p="24px" gap="8px">
          <Box fontSize="12px" fontWeight="500" color="neutral.500" textTransform="uppercase" letterSpacing="0.05em" mb="4px">
            {t('menus.share', 'Share')}
          </Box>
          <SidebarButton icon={<LuEye style={{ width: 16, height: 16 }} />} label={t('menus.viewLive', 'View Live')} onClick={() => window.open(`/m/${menu.slug}`, '_blank')} />
          <SidebarButton icon={<LuQrCode style={{ width: 16, height: 16 }} />} label={t('menus.qrCode', 'QR Code')} onClick={() => setShowQr(true)} />

          <Box borderBottomWidth="1px" borderColor="neutral.700" my="8px" />

          <Box fontSize="12px" fontWeight="500" color="neutral.500" textTransform="uppercase" letterSpacing="0.05em" mb="4px">
            {t('menus.customize', 'Customize')}
          </Box>
          <SidebarButton icon={<LuPalette style={{ width: 16, height: 16 }} />} label={t('menus.theme', 'Theme')} onClick={openThemeModal} />
          <SidebarButton icon={<LuInfo style={{ width: 16, height: 16 }} />} label={t('menus.storeInfo', 'Store Info')} onClick={() => setShowStoreInfo(true)} />
          <SidebarButton icon={<LuSettings style={{ width: 16, height: 16 }} />} label={t('menus.settings', 'Settings')} onClick={() => setShowSettings(true)} />
        </Flex>
      </Flex>

      {/* Mobile layout */}
      <Flex flex="1" direction="column" overflow="auto" display={{ base: 'flex', md: 'none' }}>
        <Box width="100%" pb="80px" position="relative" flex="1">
          <Box
            borderWidth={isEditMode ? '2px' : '0px'}
            borderColor="neutral.100"
            borderRadius={isEditMode ? '8px' : '0px'}
            overflow="hidden"
            position="relative"
          >
            {isEditMode && (
              <Flex
                alignItems="center"
                justifyContent="center"
                py="6px"
                px="12px"
                backgroundColor="neutral.100"
                color="neutral.900"
                fontSize="11px"
                fontWeight="600"
                letterSpacing="0.05em"
                textTransform="uppercase"
                gap="12px"
              >
                {menuContentCategories.length > 1 ? (
                  <Flex backgroundColor="neutral.300" borderRadius="6px" p="2px" gap="2px">
                    {pageOptions.map(({ page, label }) => (
                      <Box
                        key={page}
                        px="12px"
                        py="3px"
                        borderRadius="4px"
                        fontSize="11px"
                        fontWeight="600"
                        cursor="pointer"
                        color={currentPage === page ? 'neutral.900' : 'neutral.500'}
                        backgroundColor={currentPage === page ? 'white' : 'transparent'}
                        onClick={() => setCurrentPage(page)}
                        letterSpacing="0.03em"
                      >
                        {label}
                      </Box>
                    ))}
                  </Flex>
                ) : (
                  <Box>{t('menus.edit')} Mode</Box>
                )}
              </Flex>
            )}
            <MenuContent {...mobileMenuContentProps} />
          </Box>
        </Box>
      </Flex>

      {/* Mobile bottom tab bar */}
      <Flex
        display={{ base: 'flex', md: 'none' }}
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        height="64px"
        backgroundColor="neutral.800"
        borderTopWidth="1px"
        borderColor="neutral.700"
        alignItems="center"
        justifyContent="space-around"
        px="8px"
        zIndex="10"
      >
        <MobileTab icon={<LuType style={{ width: 20, height: 20 }} />} label={t('menus.quickEdit', 'Quick Edit')} active={isMobileQuickEditMode} onClick={() => {
          const next = !isMobileQuickEditMode;
          setIsMobileQuickEditMode(next);
          if (next) setIsEditMode(false);
        }} />
        <MobileTab icon={<LuPencil style={{ width: 20, height: 20 }} />} label={t('menus.edit')} active={isEditMode} onClick={() => {
          const next = !isEditMode;
          setIsEditMode(next);
          if (next) setIsMobileQuickEditMode(false);
        }} />
        <MobileTab icon={<LuPalette style={{ width: 20, height: 20 }} />} label={t('menus.theme', 'Theme')} onClick={openThemeModal} />
        <MobileTab icon={<LuInfo style={{ width: 20, height: 20 }} />} label={t('menus.storeInfo', 'Store Info')} onClick={() => setShowStoreInfo(true)} />
        <MobileTab icon={<LuQrCode style={{ width: 20, height: 20 }} />} label="QR" onClick={() => setShowQr(true)} />
        <MobileTab icon={<LuSettings style={{ width: 20, height: 20 }} />} label={t('menus.settings', 'Settings')} onClick={() => setShowSettings(true)} />
      </Flex>

      {/* Modals */}
      <SettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        menu={menu}
        onMenuUpdate={setMenu}
        onDeleteMenu={handleDeleteMenu}
      />

      <StoreInfoModal
        open={showStoreInfo}
        onClose={() => setShowStoreInfo(false)}
        menu={menu}
        onMenuUpdate={setMenu}
      />

      <ThemeModal
        open={showTheme}
        onClose={() => setShowTheme(false)}
        menu={menu}
        menuTheme={menuTheme}
        badgeDefinitions={badgeDefinitions}
        menuContentCategories={menuContentCategories}
        availableLanguages={availableLanguages}
        defaultLanguage={defaultLanguage}
        viewLang={viewLang}
        getTranslated={getTranslated}
        onMenuUpdate={setMenu}
        onThemeUpdate={setMenuTheme}
        onBadgesUpdate={setBadgeDefinitions}
        draftTheme={draftTheme}
        onDraftThemeChange={setDraftTheme}
        draftBadges={draftBadges}
        onDraftBadgesChange={setDraftBadges}
      />

      <CategoryModal
        open={!!editingCategory || isCreatingCategory}
        onClose={closeCategoryModal}
        menuId={menuId!}
        category={editingCategory}
        isCreating={isCreatingCategory}
        availableLanguages={availableLanguages}
        defaultLanguage={defaultLanguage}
        pendingImageUrl={pendingCategoryImageUrl}
        onSaved={async (cat, isNew) => {
          if (isNew) {
            setMenu(prev => prev ? { ...prev, categories: [...(prev.categories || []), cat] } : prev);
            if (pendingCategoryImage && menuId) {
              try {
                const updated = await uploadCategoryImage(menuId, cat.id, pendingCategoryImage);
                setMenu(prev => prev ? {
                  ...prev, categories: prev.categories?.map(c => c.id === cat.id ? updated : c),
                } : prev);
              } catch (error) {
                toast.error(t('menus.updateCategoryFailed'));
                console.error(error);
              }
              setPendingCategoryImage(null);
            }
          } else {
            setMenu(prev => prev ? { ...prev, categories: prev.categories?.map(c => c.id === cat.id ? cat : c) } : prev);
          }
        }}
        onDelete={handleDeleteCategory}
        onImageReady={handleCategoryImageReady}
        onRemoveImage={handleRemoveCategoryImage}
      />

      <ItemModal
        open={!!editingItem || isCreatingItem}
        onClose={closeItemModal}
        menuId={menuId!}
        item={editingItem}
        isCreating={isCreatingItem}
        categoryId={creatingItemCategoryId}
        availableLanguages={availableLanguages}
        defaultLanguage={defaultLanguage}
        currency={menu.currency}
        badgeDefinitions={badgeDefinitions}
        onSaved={(item, isNew, catId) => {
          if (isNew && catId) {
            setMenu(prev => prev ? {
              ...prev,
              categories: prev.categories?.map(c => c.id === catId ? { ...c, items: [...c.items, item] } : c),
            } : prev);
          } else {
            setMenu(prev => prev ? {
              ...prev,
              categories: prev.categories?.map(c => ({
                ...c, items: c.items.map(i => i.id === item.id ? item : i),
              })),
            } : prev);
          }
        }}
        onDelete={handleDeleteItem}
        onImageReady={handleItemImageReady}
        onRemoveImage={handleRemoveImage}
      />

      <QrCodeModal open={showQr} onClose={() => setShowQr(false)} slug={menu.slug} />

      {/* Image picker for quick edit image replacement */}
      <ImagePickerModal
        open={showQuickEditImagePicker}
        onClose={() => { setShowQuickEditImagePicker(false); setQuickEditImageTarget(null); }}
        onImageReady={handleQuickEditImageReady}
        aspectRatio={quickEditImageTarget?.type === 'header' ? 2 : 1}
      />
    </Flex>
  );
};

// --- Small layout components ---

const SidebarButton = ({
  icon,
  label,
  onClick,
  active,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
}) => (
  <Flex
    alignItems="center"
    gap="10px"
    px="12px"
    py="10px"
    borderRadius="8px"
    cursor="pointer"
    color={active ? 'neutral.900' : 'neutral.300'}
    backgroundColor={active ? 'neutral.100' : 'transparent'}
    _hover={active ? { backgroundColor: 'neutral.200' } : { backgroundColor: 'neutral.700' }}
    onClick={onClick}
  >
    {icon}
    <Box fontSize="14px" fontWeight="500">{label}</Box>
  </Flex>
);

const MobileTab = ({
  icon,
  label,
  onClick,
  active,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
}) => (
  <Flex
    direction="column"
    alignItems="center"
    gap="4px"
    cursor="pointer"
    color={active ? 'neutral.900' : 'neutral.400'}
    backgroundColor={active ? 'neutral.100' : 'transparent'}
    borderRadius="8px"
    onClick={onClick}
    flex="1"
    py="8px"
  >
    {icon}
    <Box fontSize="11px" fontWeight="500">{label}</Box>
  </Flex>
);

export default MenuDetail;
